import { PROXIMITY_TYPES, GEO_BOUNDARIES_TYPES } from '@/constants'
import { ONE_MILE_TO_METER } from '@/utils/metersToPixels'
import { removeGeoboundaryCacheProperties } from '@/plan/geometries/geoboundary.helper'
import type { Filter } from '@/plan/filter/filter.type'
import type { PoisFilter } from '@/plan/filter/pois.type'
import {
  Geoboundary,
  AudiencesQuintile as AudiencesQuintileNs,
  Poi,
} from '@workspaces/types'
import type { Feature } from 'geojson'
import type {
  SerializedAudiences,
  SerializedFilter,
  SerializedProximity,
  SerializedProximityCloseTo,
  SubPlanFilters,
} from './serializeFilters.type'

export function serializeFilters(planFilters: Filter[]): SerializedFilter[] {
  const filtersWithoutGeoboundaryCache =
    removeGeoboundaryCacheProperties(planFilters)

  const filtersSerialized = filtersWithoutGeoboundaryCache.map(
    (subPlanFilters: SubPlanFilters) => {
      return {
        tabname: subPlanFilters.tabname || '',
        countries: serializeCountries(subPlanFilters.countries),
        geoboundaries: serializeGeoboundaries(subPlanFilters.geoboundaries),
        audiences: serializeAudiences(subPlanFilters),
        types: serializePanelTypes(subPlanFilters),
        excluded: subPlanFilters.assets_excluded,
        is_digital: serializeDigitalPanel(subPlanFilters.isDigital),
        included: subPlanFilters.assets_included,
        panels: subPlanFilters.panels,
        geographical_distribution: serializeGeographicalDistribution(
          subPlanFilters.geographical_distribution,
        ),
        panelsExcluded: subPlanFilters.panelsExcluded,
        polygon_geom: serializePolygonGeom(subPlanFilters.polygon_geom),
        ...serializeProximity(subPlanFilters.proximity),
        audiencesQuintile: serializeAudiencesQuintile(
          subPlanFilters.audiencesQuintile,
        ),
        restrictions: serializePanelRestrictions(subPlanFilters),
      }
    },
  )
  return filtersSerialized
}

// SERIALIZE SUB FUNCTIONS

function serializeAudiencesQuintile(
  audiencesQuintile: AudiencesQuintileNs.PlanFilterParam | null | undefined,
): string | null {
  if (!audiencesQuintile) {
    return null
  }

  const serialized = JSON.stringify(audiencesQuintile)
  return serialized
}

function serializePolygonGeom(polygonGeom: Filter['polygon_geom']): string {
  if (typeof polygonGeom === 'string') {
    return polygonGeom
  } else {
    const polygons =
      serializePolygons((polygonGeom || {}).features || []) || undefined
    return JSON.stringify(polygons)
  }
}
function serializeGeographicalDistribution(
  geographicalDistribution: Filter['geographical_distribution'],
): Filter['geographical_distribution'] | Record<string, never> {
  return geographicalDistribution || {}
}

function serializeDigitalPanel(isDigital: Filter['isDigital']): boolean | '' {
  return isDigital === true || isDigital === false ? isDigital : ''
}

function serializePanelTypes(plan: SubPlanFilters): Filter['types'] {
  return plan.types
}

function serializePanelRestrictions(
  plan: SubPlanFilters,
): Filter['restrictions'] {
  return plan.restrictions || []
}

interface FilteredGeoboundaryItem {
  countries?: { id: number }[]
}

interface FilteredCountry {
  id: number
}

export function getFilteredGeoboundaries({
  countries,
  geoboundaries,
}: {
  countries: FilteredCountry[]
  geoboundaries: Record<string, FilteredGeoboundaryItem[]>
}): Record<string, FilteredGeoboundaryItem[]> {
  const filteredBoundaries: Record<string, FilteredGeoboundaryItem[]> = {}
  for (const type in geoboundaries) {
    filteredBoundaries[type] = geoboundaries[type].filter((g) => {
      if (!g.countries) {
        return true
      }
      const countryId = g.countries[0].id
      return countries.some((c) => c.id === countryId)
    })
  }
  return filteredBoundaries
}

function serializeDistance(distance: number): number {
  if (!distance) {
    const meters =
      Math.round((distance * ONE_MILE_TO_METER) / RADIUS_DISTANCE_STEP) *
      RADIUS_DISTANCE_STEP
    return meters
  }
  return 0
}

function serializeGeoboundaries(
  geoboundaries: Geoboundary.AssetFilterGeoboundaries = {} as Geoboundary.AssetFilterGeoboundaries,
): Geoboundary.AssetFilterGeoboundaries {
  const serializedBoundaries: Record<
    string,
    Geoboundary.AssetFilterGeoboundaryContent[]
  > = { ...geoboundaries }
  for (const type in serializedBoundaries) {
    // Preserve original runtime behavior: the top-level FILE key does not exist
    // on GEO_BOUNDARIES_TYPES (it lives under EU), so this resolves to undefined.
    if (type === (GEO_BOUNDARIES_TYPES as { FILE?: string }).FILE) {
      continue
    }
    const boundariesInType = serializedBoundaries[type]
    const data = boundariesInType.map((boundary) => {
      if (!boundary.type) {
        boundary.type = null as unknown as string
      }
      return boundary
    })
    serializedBoundaries[type] = data.length ? data : []
  }

  // serializedBoundaries is a loose region-keyed record; cast back to the
  // canonical shape (regions are dynamic keys handled via for..in above).
  return serializedBoundaries as unknown as Geoboundary.AssetFilterGeoboundaries
}

function serializeAudiences(filters: SubPlanFilters): SerializedAudiences {
  const andGroups = (filters.audiences || []).map((a) => a.ids.join(','))
  const orGroups = (filters.audiencesGroups || [])
    .map((a) => a.ids.join(','))
    .join(',')
  const selection = andGroups.concat(orGroups).join('^')
  return {
    profiledata_index_percent:
      filters.audiencesType === 'percent' ? filters.audiencesPercent : null, // 20,
    profile_classes: selection || null,
    profiledata_index__gte:
      selection && filters.audiencesType === 'index'
        ? filters.audiencesIndex
        : null,
    audiencesIndex: filters.audiencesIndex,
    audiencesPercent: filters.audiencesPercent,
    audiencesType: filters.audiencesType,
    audiencesGroups: filters.audiencesGroups || [],
    audiences: filters.audiences || null,
    delivery: filters.delivery || null,
    userAudiencesGroup: filters.userAudiencesGroup || [],
  }
}

function serializeCountries(data: Filter['countries']): Filter['countries'] {
  return data || []
}

function serializePolygons(features: Feature[]): Feature[] {
  return features.map((f) => {
    if (f.properties?.radius) {
      return {
        type: 'Feature',
        properties: {
          radius: f.properties.radius,
          units: f.properties.units,
          name: f.properties.name,
          center: f.properties.center,
        },
        geometry: {
          type: 'Point',
          coordinates: f.properties.coordinates,
        },
        id: f.id,
      } as Feature
    }
    return f
  })
}

const RADIUS_DISTANCE_STEP = 5
function serializeProximity(
  proximity: PoisFilter | null | undefined,
): SerializedProximity | undefined {
  if (!proximity || !proximity.data.length) {
    return undefined
  }
  const categories = proximity.data
    .filter((d) => d.type === PROXIMITY_TYPES.CATEGORIES)
    .map((d) => d.id)
  const chains = proximity.data
    .filter((d) => d.type === PROXIMITY_TYPES.BRANDS)
    .map((d) => d.id)
  const custom = proximity.data
    .filter((d) => d.type === PROXIMITY_TYPES.CUSTOM_POIS)
    .map((d) => d.id)
  const locations = proximity.data
    .filter((d) => d.type === PROXIMITY_TYPES.LOCATIONS)
    .map((d) => d.id)
  const serialized: SerializedProximityCloseTo = {
    region_type: 'HerePoi',
    types: {
      categories: categories.length ? categories : undefined,
      chains: chains.length ? chains : undefined,
      custompois_groups: custom.length ? custom : undefined,
      locations: locations.length ? locations : undefined,
    },
    mode: proximity.method,
    distance: proximity.distance,
    operation: proximity.operation,
  }
  if (serialized.mode === Poi.POIsFilterMethod.Radius) {
    serialized.distance = serializeDistance(serialized.distance)
  }

  return {
    close_to: serialized,
    meta_proximity: proximity,
  }
}
